import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';

import type { FilterOption } from '../../../types/FilterOption';

import { NetworkStatus } from '@apollo/client/core';
import { useQuery } from '@apollo/client/react';
import {
    FactoryTypeEnum,
    GetAllSuppliersDocument,
    GraphClientNames,
    toFilterOptions,
} from '@buying/library';

interface UseSuppliersFilterResult {
    /** The options returned by the query */
    suppliersOptions: Array<FilterOption>;
    /** Returns true when the search query was changed until the new query's data has been loaded */
    isSearching: boolean;
    /** Function to directly update the searchQuery value used for the query */
    setSearchQuery: Dispatch<SetStateAction<string>>;
}

interface QueryOptions {
    /** Whether to skip the supplier query. */
    skip?: boolean;
    /** The factory type to include in the supplier query. */
    factoryTypeToInclude?: FactoryTypeEnum;
}

/**
 * Fetches supplier options for filter bar usage, with debounced search.
 * Queries the basic-data BFF for suppliers matching the given brand and search query.
 *
 * @param brandNumber - The brand number to filter suppliers by. Defaults to empty string.
 * @param queryOptions - Optional supplier query configuration. Defaults to skipping when brandNumber is empty.
 * @returns suppliersOptions - The supplier filter options from the query.
 * @returns isSearching - True from when the search query changes until the debounced query finishes loading.
 * @returns setSearchQuery - Setter to update the search query (debounced internally before querying).
 */
const useSuppliersFilter = (
    brandNumber: string | undefined = '',
    queryOptions: QueryOptions = {}
): UseSuppliersFilterResult => {
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

    const { skip, factoryTypeToInclude = FactoryTypeEnum.Purchase } =
        queryOptions;

    const { data: suppliersData, networkStatus } = useQuery(
        GetAllSuppliersDocument,
        {
            fetchPolicy: 'cache-and-network',
            notifyOnNetworkStatusChange: true,
            skip: skip ?? !brandNumber,
            context: {
                clientName:
                    GraphClientNames['bestone-bi4-buying-basic-data-bff'],
            },
            variables: {
                brandNumber,
                searchQuery: debouncedSearchQuery,
                isDeleted: false,
                factoryTypeToInclude: factoryTypeToInclude,
            },
        }
    );

    const isSearching =
        searchQuery !== debouncedSearchQuery ||
        networkStatus === NetworkStatus.setVariables;

    const suppliersOptions: Array<FilterOption> = toFilterOptions({
        items: suppliersData?.suppliers,
        label: (supplier) => supplier.supplierName,
        value: (supplier) => supplier.supplierNumber,
    });

    // Debounce the search query update for suppliers
    useEffect((): (() => void) => {
        const timeoutId = globalThis.setTimeout((): void => {
            setDebouncedSearchQuery(searchQuery);
        }, 300);

        return (): void => {
            globalThis.clearTimeout(timeoutId);
        };
    }, [searchQuery]);

    return {
        suppliersOptions,
        isSearching,
        setSearchQuery,
    };
};

export { useSuppliersFilter };
